import { useEffect, useRef } from 'react'
import { observeGameSession } from '../../apiLayer/handlers/directDbHandler'
import { WherebyCallKind } from '../../../../pure-js/enums/Whereby'
import { useGlobalWhereby } from './useGlobalWhereby'

// Pure guard (exported for tests): leave only a live GAME call, and only once the backend has marked the
// pinned game session 'completed'. Never touches an AI-coach/team call or an active (still-playing) game.
export const shouldLeaveCallForGameSession = (
  state: { isCallActive: boolean; callScopeKind?: string },
  sessionStatus?: string
): boolean => state.isCallActive && state.callScopeKind === WherebyCallKind.GAME && sessionStatus === 'completed'

// While holding a GAME call, watch the pinned game's session document. The backend marks it
// `status: 'completed'` when the game ends (/Session/Close, or the scheduled auto-complete). When that
// happens, leave the call so a lingering tab (e.g. a forgotten second tab) can't keep the game room alive
// and block the AI coach — WITHOUT deleting the Whereby room (which would discard the transcript). The room
// then empties naturally → Whereby finalises the transcript → the AI coach pipeline runs.
export const useLeaveGameCallWhenSessionCompleted = (): void => {
  const { callScope, isCallActive, killCall, setCallScope } = useGlobalWhereby()

  const isGameCall = isCallActive && callScope?.kind === WherebyCallKind.GAME
  const teamId = isGameCall ? callScope?.teamId : undefined
  const sessionId = isGameCall ? callScope?.sessionId : undefined

  // Latest state for the snapshot callback to read without re-subscribing.
  const stateRef = useRef({ isCallActive, kind: callScope?.kind, killCall, setCallScope })
  stateRef.current = { isCallActive, kind: callScope?.kind, killCall, setCallScope }

  useEffect(() => {
    if (!teamId || !sessionId) return
    return observeGameSession(
      teamId,
      sessionId,
      (session) => {
        const { isCallActive: active, kind, killCall: leave, setCallScope: setScope } = stateRef.current
        if (shouldLeaveCallForGameSession({ isCallActive: active, callScopeKind: kind }, session?.status)) {
          leave()
          // The pin decides which room this tab belongs in and outlives the call, so it has to go too —
          // otherwise the game page keeps resolving to the finished game's room.
          setScope(null)
        }
      },
      (error) => console.error('observeGameSession failed', error)
    )
  }, [teamId, sessionId])
}
